import levenshtein from 'js-levenshtein';

import {
  addQuestionMessage,
  getQuestion,
  setMessage,
  setMessageVisibility,
} from 'app/services/businessFunctionHelpers';
import {
  FieldsEnum,
  FormsEnum,
  MessageTypeEnum,
  ValidationErrorMessagesEnum,
} from 'app/shared/enums';
import { getHashedMessageSync } from 'app/shared/helpers/getHashedMessage';
import { isElementInViewport } from 'app/shared/helpers/isElementInViewport';
import { getEmailTypoMessageKey } from 'app/shared/helpers/emailTypoMessage';

const commonEmailDomains = [
  'gmail.com',
  'wp.pl',
  'o2.pl',
  'interia.pl',
  'op.pl',
  'onet.pl',
  'vp.pl',
  'tlen.pl',
  'poczta.onet.pl',
  'onet.eu',
  'poczta.fm',
  'icloud.com',
  'interia.eu',
  'hotmail.com',
  'outlook.com',
  'yahoo.com',
  'gazeta.pl',
  'autograf.pl',
  'spoko.pl',
  'protonmail.com',
  'int.pl',
  'yahoo.pl',
  'go2.pl',
  'onet.com.pl',
  'buziaczek.pl',
  'poczta.onet.eu',
  'wp.eu',
  'proton.me',
  'amorki.pl',
  'googlemail.com',
];

export const validateEmailDomainTypo = ({
  email,
  formName,
  questionKey,
}: {
  email: string;
  formName: FormsEnum;
  questionKey: string;
}) => {
  const typosConfirmed = sessionStorage.getItem('typosConfirmed') || '';
  const hashItem = getHashedMessageSync(email);
  const messageKey = getEmailTypoMessageKey(questionKey);

  if (typosConfirmed.includes(hashItem)) return false;

  const [user, domain] = email.split('@');

  if (/['"]/.test(user)) return false;

  let minDistance = Infinity;
  let bestMatch: string | null = null;

  for (const correct of commonEmailDomains) {
    const levenshteinDistance = levenshtein(domain, correct);
    if (
      !commonEmailDomains.includes(domain) &&
      levenshteinDistance &&
      levenshteinDistance <= 2 &&
      levenshteinDistance < minDistance
    ) {
      minDistance = levenshteinDistance;
      bestMatch = correct;
    }
  }

  if (bestMatch) {
    setMessage({
      formName,
      message: {
        content: ValidationErrorMessagesEnum.TypoInEmailDomain.replace(
          /{{correctEmail}}/g,
          `${user}@${bestMatch}`
        )
          .replace(/{{formName}}/g, formName)
          .replace(/{{questionKey}}/g, questionKey),
        key: messageKey,
        type: MessageTypeEnum.Warning,
        visible: true,
      },
    });

    const messages = getQuestion(questionKey as FieldsEnum, formName)?.messages;

    if (!(messages && messages.includes(messageKey))) {
      addQuestionMessage({
        formName,
        questionKey,
        messageKey,
      });
    }

    const element = document.querySelector<HTMLElement>(
      `div[data-field-type="question"][data-field-key="${questionKey}"]`
    );
    const elementVisible = element && isElementInViewport(element);
    if (element && !elementVisible) {
      const y = element.getBoundingClientRect().top + window.scrollY - 120;
      window.scroll({
        top: y,
        behavior: 'smooth',
      });
    }

    sessionStorage.setItem('typosConfirmed', `${hashItem},${typosConfirmed}`);
    return true;
  } else {
    setMessageVisibility({
      formName,
      key: messageKey,
      visible: false,
    });
    return false;
  }
};

export const hasUnconfirmedEmailTypo = ({
  email,
  formName,
  questionKey,
}: {
  email?: string;
  formName: FormsEnum;
  questionKey: FieldsEnum;
}) => {
  const question = getQuestion(questionKey, formName);

  if (!email || !question || !question.visible) {
    return false;
  }

  const hasSeenTypoHint = question.messages.includes(
    getEmailTypoMessageKey(questionKey)
  );
  const typoFound = validateEmailDomainTypo({ email, formName, questionKey });

  return !hasSeenTypoHint && typoFound;
};
